"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ExternalLink, KeyRound, Laptop, Play } from "lucide-react";
import { trackClientEvent } from "@/lib/analyticsClient";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  StepsCard,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { TvOverlayMock } from "@/components/marketing/dev-landing/mocks";

// Download links.
const MAC_ARM_DOWNLOAD_URL =
  "https://quantwheel.nyc3.cdn.digitaloceanspaces.com/dmg/QuantWheel%20GEX%20Bridge-0.6.2-arm64.dmg";
const MAC_INTEL_DOWNLOAD_URL =
  "https://quantwheel.nyc3.cdn.digitaloceanspaces.com/dmg/QuantWheel%20GEX%20Bridge-0.6.2.dmg";
const WIN_DOWNLOAD_URL =
  "https://quantwheel.nyc3.cdn.digitaloceanspaces.com/dmg/QuantWheel%20GEX%20Bridge%20Setup%200.6.2.exe";

// Demo video: YouTube embed of the bridge in action.
const DEMO_YOUTUBE_EMBED_URL = "https://www.youtube-nocookie.com/embed/Nq-TaiUqeS8?start=123";

const SEE_IT_IN_ACTION_URL = "https://youtu.be/Nq-TaiUqeS8?si=az1_2154IfXBM5ce&t=123";
const TRADINGVIEW_DESKTOP_URL = "https://www.tradingview.com/desktop/";

interface TradingViewGexBridgeClientProps {
  keyHref: string;
}

// Platform icons matching the TradingView desktop download buttons (28x28 viewBox).
const WINDOWS_ICON_PATH =
  "M11 3.88 2 5v8h9V3.87Zm2-.25V13h13V2L13 3.63ZM26 15H13v9.38L26 26V15Zm-15 9.13V15H2v8l9 1.13Z";
const APPLE_ICON_PATH =
  "M18.96 6.3c-2.06 0-3.7 1.2-4.78 1.2-1.12 0-2.61-1.13-4.4-1.13C6.44 6.37 3 9.07 3 14.14c0 3.18 1.26 6.5 2.82 8.66 1.35 1.82 2.53 3.32 4.2 3.32 1.69 0 2.43-1.08 4.5-1.08 2.1 0 2.59 1.05 4.44 1.05 1.82 0 3.05-1.62 4.19-3.22A13.3 13.3 0 0 0 25 19.16c-.1-.04-3.6-1.4-3.6-5.29 0-3.36 2.75-4.84 2.9-4.96a6.43 6.43 0 0 0-5.34-2.6Zm-.97-2.14A5.8 5.8 0 0 0 19.43.52c0-.19-.02-.37-.06-.52a6.08 6.08 0 0 0-3.98 1.97 5.66 5.66 0 0 0-1.49 3.55c0 .2.04.4.06.46.08.02.22.04.36.04 1.23 0 2.77-.79 3.67-1.86Z";

function DownloadButton({
  platformName,
  iconPath,
  url,
}: {
  platformName: string;
  iconPath: string;
  url: string | null;
}) {
  const className =
    "flex h-14 w-full shrink-0 items-center gap-3 whitespace-nowrap rounded-xl border border-[#3a3c3b] bg-gradient-to-b from-[#1c1e1d] to-[#131414] px-5 text-left text-[#e8eae9] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:w-auto" +
    (url
      ? " hover:border-[#4a4c4b] hover:bg-gradient-to-b hover:from-[#222423] hover:to-[#161717]"
      : " cursor-not-allowed opacity-60");

  const content = (
    <>
      <span role="img" aria-hidden="true" className="text-[#e8eae9]">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28">
          <path fill="currentColor" d={iconPath} />
        </svg>
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-[11px] text-[#9aa09e]">{url ? "Download" : "Coming soon"}</span>
        <span className="text-[15px]">{platformName}</span>
      </div>
    </>
  );

  if (!url) {
    return <div className={className}>{content}</div>;
  }
  return (
    <a href={url} className={className}>
      {content}
    </a>
  );
}

export default function TradingViewGexBridgeClient({ keyHref }: TradingViewGexBridgeClientProps) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <Laptop className="h-3.5 w-3.5 text-[#9aa09e]" />
          <span className="text-sm text-[#d4d5d5]">Desktop app for Mac &amp; Windows</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          Real-time <span style={metalText}>gamma exposure</span> on TradingView
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          The only real-time GEX, Vanna and options flows overlay for TradingView.
        </p>

        {/* CTAs */}
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="tv_bridge_get_key_clicked" />
          <a
            href={SEE_IT_IN_ACTION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto"
            onClick={() => trackClientEvent({ name: "tv_bridge_see_it_in_action_clicked" })}
          >
            <Button
              size="lg"
              variant="outline"
              className="h-12 w-full shrink-0 gap-2 whitespace-nowrap rounded-lg border-white/20 bg-transparent px-8 text-base text-white hover:border-white/40 hover:bg-white/10 sm:w-auto"
            >
              <Play className="h-4 w-4" />
              See it in action
            </Button>
          </a>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Included in Pro &amp; Quant plans.</p>

        {/* Downloads */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <DownloadButton platformName="Mac (Apple Silicon)" iconPath={APPLE_ICON_PATH} url={MAC_ARM_DOWNLOAD_URL} />
          <DownloadButton platformName="Mac (Intel)" iconPath={APPLE_ICON_PATH} url={MAC_INTEL_DOWNLOAD_URL} />
          <DownloadButton platformName="Windows" iconPath={WINDOWS_ICON_PATH} url={WIN_DOWNLOAD_URL} />
        </div>
        <p className="mt-3 text-xs text-white/40">
          Requires the free{" "}
          <a
            href={TRADINGVIEW_DESKTOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 underline-offset-4 hover:text-white/80 hover:underline"
          >
            TradingView desktop app
            <ExternalLink className="mb-0.5 ml-1 inline h-3 w-3" />
          </a>
        </p>
      </div>

      {/* Demo video */}
      <div className="relative mx-auto mt-12 w-full max-w-5xl md:mt-16">
        <div
          className="pointer-events-none absolute -inset-12 rounded-full opacity-20"
          style={{
            background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#101111] shadow-2xl shadow-black/60">
          <iframe
            src={DEMO_YOUTUBE_EMBED_URL}
            title="TradingView GEX bridge demo video"
            className="aspect-video w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>

      {/* Your chart, our levels */}
      <FeatureSplit
        eyebrow="Your chart. Our levels."
        title={
          <>
            Walls and the flip, <span style={metalText}>drawn where you trade</span>
          </>
        }
        lead="Call walls, put walls, the gamma flip, Vanna and live options flows - overlaid on your own TradingView layouts and indicators, not in another browser tab."
        points={[
          { text: "Levels stream in real time and redraw as dealer positioning shifts - no refresh, no re-import, no stale lines." },
          { text: "Change the ticker on TradingView and the overlay follows instantly, every time." },
          { text: "Works with your existing layouts, drawings and indicators - our levels sit on top." },
        ]}
        visual={<TvOverlayMock />}
      />

      {/* How it works */}
      <StepsCard
        title="Up and running in minutes"
        steps={[
          {
            title: "Download & install the app",
            description:
              "Grab the QuantWheel GEX Bridge for Mac (Apple Silicon or Intel) or Windows above and run it.",
          },
          {
            title: "Install TradingView Desktop",
            description: (
              <>
                Grab the free app from{" "}
                <a
                  href={TRADINGVIEW_DESKTOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7fe7c8] underline-offset-4 hover:underline"
                >
                  tradingview.com/desktop
                </a>{" "}
                - the bridge runs right next to it.
              </>
            ),
          },
          {
            title: "Get your QuantWheel key",
            description: (
              <>
                Paste your key once in the bridge and the overlay goes live on your charts.
                <Link href={keyHref} className="mt-3 block">
                  <Button
                    size="sm"
                    className="gap-1.5 whitespace-nowrap rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm hover:opacity-90"
                    onClick={() =>
                      trackClientEvent({
                        name: "tv_bridge_get_key_clicked",
                        params: { href: keyHref, location: "steps" },
                      })
                    }
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    Get your key
                  </Button>
                </Link>
              </>
            ),
          },
        ]}
      />

      <div className="mt-20 sm:mt-24">
        <TextFeatureList
          items={[
            {
              title: "Runs quietly alongside",
              description: (
                <>
                  The bridge is a small desktop companion to the free{" "}
                  <a
                    href={TRADINGVIEW_DESKTOP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#7fe7c8] underline-offset-4 hover:underline"
                  >
                    TradingView desktop app
                  </a>{" "}
                  for Mac &amp; Windows - no browser extensions, no Pine scripts to maintain.
                </>
              ),
            },
            {
              title: "Powered by the same API",
              description:
                "The overlay draws the exact levels the QuantWheel GEX API serves - the same key unlocks both, so what your bot sees is what your chart shows.",
            },
          ]}
        />
      </div>

      <CrossSellBand
        badge="Build on the data instead"
        title="The levels behind the overlay, as JSON"
        description="Walls, flips, heatmaps and events are all endpoints on the QuantWheel GEX API - same engine, same key, your code."
        href="/gex-api"
        linkLabel="Explore the GEX API"
      />

      <FinalCta
        title="Ready to see gamma on your charts?"
        sub="One key connects the bridge to your QuantWheel account."
        keyHref={keyHref}
        eventName="tv_bridge_get_key_clicked"
        note="Included in Pro & Quant plans."
      />
    </DevLandingShell>
  );
}
